import React, { useState, useCallback, useMemo } from 'react';
import { Card, Button, Table, Pagination, Modal } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { getToken } from 'actions';
import Reload from '../../../../components/Reload/Reload';
import dateformat from "utils/dateformat";
import {
  getTableHeaderClass,
  getModernDataTableClass,
  getModernCardBodyClass,
  getModernCardFooterClass,
  getModernTableClass,
  getModernDataTableMetaClass,
} from 'utils/tableTheme';

// Backend a implementar: GET com token na query (mesmo padrão do download JSON por serial).
const QR_COMPLETOS_DOWNLOAD_PATH = '/measurements-nir/v1/download/qr-laboratorio-e-classificacao';

const QrResults = ({
  user,
  data,
  currentTheme,
  loading,
  downloadsDisabled = false,
  disableDetailsClick = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [orderDirection, setOrderDirection] = useState('desc');

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleChangePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  }, []);

  const handleSort = useCallback((column) => {
    const isAsc = orderBy === column && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
    setPage(0);
  }, [orderBy, orderDirection]);

  const getSortIcon = (column) => {
    if (orderBy !== column) return <FaSort className="ms-1" />;
    return orderDirection === 'asc' ?
      <FaSortUp className="ms-1" /> :
      <FaSortDown className="ms-1" />;
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    const countMeasures = (arr) => {
      if (!Array.isArray(arr)) return 0;
      return arr.reduce((acc, item) => acc + (Array.isArray(item?.measures) ? item.measures.length : 0), 0);
    };

    return [...data].sort((a, b) => {
      let valueA, valueB;

      switch (orderBy) {
        case 'qrcode':
          valueA = a.qrcode || '';
          valueB = b.qrcode || '';
          break;
        case 'createdAt':
          valueA = new Date(a.createdAt || 0).getTime();
          valueB = new Date(b.createdAt || 0).getTime();
          break;
        case 'classificationsMeasures':
          valueA = countMeasures(a.classifications);
          valueB = countMeasures(b.classifications);
          break;
        case 'laboratoryMeasures':
          valueA = countMeasures(a.laboratory);
          valueB = countMeasures(b.laboratory);
          break;
        case 'currentStatus':
          valueA = a.currentStatus || '';
          valueB = b.currentStatus || '';
          break;
        case 'farmerName':
          valueA = a.farmer?.name || '';
          valueB = b.farmer?.name || '';
          break;
        default:
          valueA = a[orderBy] || '';
          valueB = b[orderBy] || '';
      }

      if (valueA < valueB) {
        return orderDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return orderDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, orderBy, orderDirection]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const getPaginationRange = () => {
    const start = Math.max(page - 2, 0);
    const end = Math.min(page + 2, totalPages - 1);
    let range = [];
    for (let i = start; i <= end; i++) range.push(i);
    return range;
  };

  const handleRowClick = useCallback((item) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
  }, []);

  const handleDownloadQrLabEClassificacao = () => {
    const base = process.env.REACT_APP_API_DATA_ENGINE || '';
    const url = `${base}${QR_COMPLETOS_DOWNLOAD_PATH}?token=${getToken()}`;
    window.open(url, '_blank');
  };

  if (loading && data.length === 0) return <Reload />;

  return (
    <div>
      <p className={getModernDataTableMetaClass()}>
        {data.length} registros encontrados. Página {page + 1} de {totalPages || 1}
      </p>
      <Card className={getModernDataTableClass(currentTheme)}>
        <Card.Header
          className={
            currentTheme === 'dark'
              ? 'bg-dark text-light border-secondary d-flex flex-wrap justify-content-between align-items-center gap-2'
              : 'bg-light text-dark d-flex flex-wrap justify-content-between align-items-center gap-2'
          }
        >
          <span>Resultados por Qrcode</span>
          <Button
            size="sm"
            variant={currentTheme === 'dark' ? 'outline-light' : 'outline-success'}
            title="Baixa os QRs que possuem medida de laboratório e de classificação preenchidas"
            onClick={handleDownloadQrLabEClassificacao}
            disabled={downloadsDisabled || !user?.permission?.nir?.write}
          >
            Download QR (lab. + classif.)
          </Button>
        </Card.Header>
        <Card.Body className={getModernCardBodyClass(currentTheme)}>
          <Table
            style={{ fontSize: "0.9rem" }}
            striped bordered hover responsive size="sm"
            className={getModernTableClass(currentTheme)}
          >
            <thead>
              <tr className={getTableHeaderClass(currentTheme)}>
                <th className='text-center' onClick={() => handleSort('qrcode')} style={{ cursor: 'pointer' }}>
                  QRCODE {getSortIcon('qrcode')}
                </th>
                <th className='text-center' onClick={() => handleSort('currentStatus')} style={{ cursor: 'pointer' }}>
                  STATUS {getSortIcon('currentStatus')}
                </th>
                <th className='text-center' onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                  ATUALIZADO EM {getSortIcon('createdAt')}
                </th>
                <th className='text-center' onClick={() => handleSort('classificationsMeasures')} style={{ cursor: 'pointer' }}>
                  MED. CLASSIF. {getSortIcon('classificationsMeasures')}
                </th>
                <th className='text-center' onClick={() => handleSort('laboratoryMeasures')} style={{ cursor: 'pointer' }}>
                  MED. LAB. {getSortIcon('laboratoryMeasures')}
                </th>
                <th className='text-center' onClick={() => handleSort('farmerName')} style={{ cursor: 'pointer' }}>
                  PRODUTOR {getSortIcon('farmerName')}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">Nenhum resultado para exibir. Utilize a busca acima.</td>
                </tr>
              )}
              {paginatedData.map((item, idx) => (
                <tr
                  key={item._id || idx}
                  onClick={disableDetailsClick ? undefined : () => handleRowClick(item)}
                  style={{ cursor: disableDetailsClick ? 'default' : 'pointer' }}
                  title={disableDetailsClick ? undefined : 'Clique para ver detalhes'}
                >
                  <td className='text-center pt-2 fw-bold'>{item.qrcode || '--'}</td>
                  <td className='text-center pt-2'>{item.currentStatus || '--'}</td>
                  <td className='text-center pt-2'>{dateformat(item.createdAt)}</td>
                  <td className='text-center pt-2'>
                    {(Array.isArray(item.classifications) ? item.classifications.reduce((acc, c) => acc + (Array.isArray(c?.measures) ? c.measures.length : 0), 0) : 0)}
                  </td>
                  <td className='text-center pt-2'>
                    {(Array.isArray(item.laboratory) ? item.laboratory.reduce((acc, l) => acc + (Array.isArray(l?.measures) ? l.measures.length : 0), 0) : 0)}
                  </td>
                  <td className='text-center pt-2'>{item.farmer?.name || '--'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        {totalPages > 1 && (
          <Card.Footer className={getModernCardFooterClass(currentTheme)}>
            <div>
              <label>
                Linhas: {' '}
                <select className="modern-data-table__rows-select" value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                  {[20, 40, 60].map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination className="custom-pagination">
              <Pagination.Prev onClick={() => handleChangePage(page - 1)} disabled={page === 0} />
              {getPaginationRange().map((index) => (
                <Pagination.Item key={index} active={index === page} onClick={() => handleChangePage(index)}>
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next onClick={() => handleChangePage(page + 1)} disabled={page >= totalPages - 1} />
            </Pagination>
          </Card.Footer>
        )}
      </Card>

      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="lg"
        centered
        contentClassName={currentTheme === 'dark' ? 'bg-dark text-light' : ''}
      >
        <Modal.Header closeButton className={currentTheme === 'dark' ? 'bg-dark text-light border-secondary' : ''}>
          <Modal.Title>Detalhes do Package</Modal.Title>
        </Modal.Header>
        <Modal.Body className={currentTheme === 'dark' ? 'bg-dark text-light' : ''}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13 }}>
            {selectedItem ? JSON.stringify(selectedItem, null, 2) : '--'}
          </pre>
        </Modal.Body>
        <Modal.Footer className={currentTheme === 'dark' ? 'bg-dark text-light border-secondary' : ''}>
          <Button
            variant={currentTheme === 'dark' ? 'outline-light' : 'secondary'}
            onClick={() => setShowDetailsModal(false)}
          >
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QrResults;
